import React from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, ImageUp, Info, Loader2, RotateCcw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { ConfirmDialog } from '@/components/features/bookings/ConfirmDialog'
import { sdk } from '@/lib/sdk'
import { cn } from '@/lib/utils'
import type { RecognitionType, RecognitionTypeInfo } from '@/types/recognition'
import { RecognitionArt } from './RecognitionArt'

/** The API's limits (`recognitionTypes` service); it checks them again and answers 400 with its own text. */
const ART_LIMITS = Object.freeze({
  maxBytes: 5 * 1024 * 1024,
  minWidth: 1200,
  mimeTypes: ['image/png', 'image/jpeg', 'image/webp'] as readonly string[]
})

const errorOf = (err: unknown, t: (key: string) => string): string => {
  const e = err as { response?: { status?: number; data?: { message?: string } } }
  const message = e?.response?.data?.message
  if (message) return message
  return e?.response?.status === 403 ? t('types.errors.forbidden') : t('types.errors.failed')
}

/** Reads the picked file's width in the browser, to say "too small" before the upload. */
const widthOf = (url: string): Promise<number> => new Promise((resolve) => {
  const img = new Image()
  img.onload = () => resolve(img.naturalWidth)
  img.onerror = () => resolve(0)
  img.src = url
})

interface Pending {
  type: RecognitionType
  file: File
  url: string
  error: string
}

/**
 * F-185 "Tipos e artes" (owner/admin): the seven kinds, community-wide, with
 * the art in use, on/off, an own art upload with a preview of the 1.91:1 crop
 * and the band, and "Voltar para a padrão". Each action saves at once through
 * its own endpoint (it is not part of the space form). The role rule is the
 * server's: a 403 shows as an error, never a hidden success.
 */
export const RecognitionTypesSettings = () => {
  const { t } = useTranslation('recognition')
  const [types, setTypes] = React.useState<RecognitionTypeInfo[] | null>(null)
  const [loadError, setLoadError] = React.useState(false)
  const [busy, setBusy] = React.useState<RecognitionType | null>(null)
  const [rowError, setRowError] = React.useState<{ type: RecognitionType; message: string } | null>(null)
  const [pending, setPending] = React.useState<Pending | null>(null)
  const [resetting, setResetting] = React.useState<RecognitionTypeInfo | null>(null)
  const [resetError, setResetError] = React.useState<string | null>(null)
  const fileInput = React.useRef<HTMLInputElement>(null)
  const pickFor = React.useRef<RecognitionType | null>(null)

  const load = React.useCallback(async () => {
    setLoadError(false)
    try {
      setTypes(await sdk.recognitions.listTypes())
    } catch {
      setLoadError(true)
    }
  }, [])

  React.useEffect(() => { load() }, [load])
  React.useEffect(() => () => { if (pending) URL.revokeObjectURL(pending.url) }, [pending])

  const replace = (next: RecognitionTypeInfo) => setTypes((list) => (list ? list.map((r) => (r.type === next.type ? next : r)) : list))

  const toggle = async (info: RecognitionTypeInfo, enabled: boolean) => {
    setBusy(info.type)
    setRowError(null)
    try {
      replace(await sdk.recognitions.setType(info.type, enabled))
    } catch (err) {
      setRowError({ type: info.type, message: errorOf(err, t) })
    } finally {
      setBusy(null)
    }
  }

  const pick = (type: RecognitionType) => {
    pickFor.current = type
    setRowError(null)
    fileInput.current?.click()
  }

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    const type = pickFor.current
    if (!file || !type) return
    const url = URL.createObjectURL(file)
    let error = ''
    if (!ART_LIMITS.mimeTypes.includes(file.type)) error = t('types.errors.invalidType')
    else if (file.size > ART_LIMITS.maxBytes) error = t('types.errors.tooLarge')
    else {
      const width = await widthOf(url)
      if (width > 0 && width < ART_LIMITS.minWidth) error = t('types.errors.tooSmall', { width })
    }
    setPending({ type, file, url, error })
  }

  const upload = async () => {
    if (!pending || pending.error) return
    setBusy(pending.type)
    try {
      replace(await sdk.recognitions.uploadTypeArt(pending.type, pending.file))
      setPending(null)
    } catch (err) {
      setPending((p) => (p ? { ...p, error: errorOf(err, t) } : p))
    } finally {
      setBusy(null)
    }
  }

  const reset = async () => {
    if (!resetting) return
    setBusy(resetting.type)
    setResetError(null)
    try {
      replace(await sdk.recognitions.removeTypeArt(resetting.type))
      setResetting(null)
    } catch (err) {
      setResetError(errorOf(err, t))
    } finally {
      setBusy(null)
    }
  }

  return (
    <section className="min-w-0 space-y-3 border-t border-gray-200 pt-5" data-testid="recognition-types-settings">
      <div>
        <h3 className="text-sm font-semibold text-gray-900">{t('types.title')}</h3>
        <p className="mt-1 flex items-start gap-1.5 text-xs text-gray-500">
          <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />{t('types.communityWide')}
        </p>
        <p className="mt-1 text-xs text-gray-500">{t('types.artSpec')}</p>
      </div>

      <input ref={fileInput} type="file" accept={ART_LIMITS.mimeTypes.join(',')} className="hidden" onChange={onFile} data-testid="recognition-art-input" />

      {loadError ? (
        <div className="flex flex-wrap items-center gap-3 rounded-lg bg-red-50 p-3 text-sm text-red-600" role="alert">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />{t('types.errors.load')}
          <Button type="button" size="sm" variant="outline" onClick={load}>{t('common:retry')}</Button>
        </div>
      ) : !types ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => <div key={i} className="h-20 animate-pulse rounded-lg bg-gray-100" />)}
        </div>
      ) : (
        <ul className="space-y-2">
          {types.map((info) => {
            const isBusy = busy === info.type
            const preview = pending?.type === info.type ? pending : null
            return (
              <li key={info.type} className="min-w-0 rounded-lg border border-gray-200 p-3" data-testid="recognition-type-row" data-type={info.type}>
                <div className="flex min-w-0 items-center gap-3">
                  <RecognitionArt artUrl={info.artUrl} phrase={info.phrase} people={[]} size="thumb"
                    className={cn('w-24 flex-shrink-0 rounded-md sm:w-32', !info.enabled && 'opacity-50')} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-gray-900">{info.label}</p>
                    <p className="truncate text-xs text-gray-500">“{info.phrase}”</p>
                    <p className="mt-0.5 text-xs text-gray-500">{info.hasCustomArt ? t('types.customArt') : t('types.defaultArt')}</p>
                  </div>
                  {isBusy && <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin text-gray-400" />}
                  <Switch checked={info.enabled} disabled={isBusy} className="flex-shrink-0 data-[state=checked]:bg-primary"
                    aria-label={t('types.enabledLabel', { label: info.label })}
                    onCheckedChange={(next: boolean) => toggle(info, next)} />
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  <Button type="button" size="sm" variant="outline" className="gap-1.5" disabled={isBusy} onClick={() => pick(info.type)}>
                    <ImageUp className="h-4 w-4" />{info.hasCustomArt ? t('types.replaceArt') : t('types.uploadArt')}
                  </Button>
                  {info.hasCustomArt && (
                    <Button type="button" size="sm" variant="outline" className="gap-1.5" disabled={isBusy}
                      onClick={() => { setResetError(null); setResetting(info) }} data-testid="recognition-art-reset">
                      <RotateCcw className="h-4 w-4" />{t('types.resetArt')}
                    </Button>
                  )}
                </div>
                {rowError?.type === info.type && <p role="alert" className="mt-2 text-xs text-red-600">{rowError.message}</p>}

                {preview && (
                  <div className="mt-3 space-y-2 rounded-lg bg-gray-50 p-3" data-testid="recognition-art-preview">
                    <p className="text-xs font-medium text-gray-700">{t('types.previewTitle')}</p>
                    <RecognitionArt artUrl={preview.url} phrase={info.phrase} custom
                      people={[{ id: 'example', name: t('types.exampleName'), avatar: null }]}
                      className="max-w-md rounded-md" />
                    <p className="text-xs text-gray-500">{t('types.previewHint')}</p>
                    {preview.error && <p role="alert" className="text-sm text-red-600" data-testid="recognition-art-error">{preview.error}</p>}
                    <div className="flex flex-wrap gap-2">
                      <Button type="button" size="sm" variant="outline" disabled={isBusy} onClick={() => setPending(null)}>{t('common:cancel')}</Button>
                      <Button type="button" size="sm" variant="secondary" className="gap-1.5" disabled={isBusy || Boolean(preview.error)} onClick={upload}
                        data-testid="recognition-art-save">
                        {isBusy && <Loader2 className="h-4 w-4 animate-spin" />}{t('types.saveArt')}
                      </Button>
                    </div>
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      )}

      <ConfirmDialog
        open={Boolean(resetting)}
        onOpenChange={(open) => { if (!open) setResetting(null) }}
        title={t('types.resetTitle')}
        body={t('types.resetBody', { label: resetting?.label || '' })}
        confirmLabel={t('types.resetArt')}
        busy={busy !== null && busy === resetting?.type}
        error={resetError}
        onConfirm={reset}
      />
    </section>
  )
}
